import { Component, EventEmitter, Output, ChangeDetectorRef, ViewChild, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EfileAdminService } from '../efile-admin.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-tmef-config',
    templateUrl: './efile-tmef-config.component.html',
    styleUrls: ['./efile-tmef-config.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfileTmefConfigComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  title = 'Add/Edit Configs';
  minimise = false;
  saving = false;

  @ViewChild('tmefConfigForm') tmefConfigForm!: NgForm;

  constructor(
    private efileAdminService: EfileAdminService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.rowData = { ...row };
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {
    if (this.saving) return;
    if (this.tmefConfigForm?.invalid) {
      this.gtwSnackbarService.openSnackBar('Please fill all required fields.', 'gtw-snackbar--red');
      return;
    }
    this.saving = true;
    this.cdr.detectChanges();

    const params = {
      request_json: JSON.stringify(this.rowData),
      jcd_key: this.filterParams?.jcd_key ?? this.globalService?.jcd_key ?? 250,
      issue_id: this.filterParams?.issue_id ?? this.globalService?.issue_id ?? '',
      issue_short_desc: this.filterParams?.issue_short_desc ?? this.globalService?.issue_short_desc ?? '',
      checkLockedbj: JSON.stringify([{}])
    };

    this.efileAdminService.saveJsonObject(
      this.GENERAL_CONFIG?.base_url ?? '',
      32894,
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.saving = false;
        this.cdr.detectChanges();
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = (data?.errorMessage && data.errorMessage !== 'null') ? data.errorMessage : 'Error saving.';
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.saving = false;
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    });
  }
}
